/**
 * Preloader Vuex Module
 * Manages preloader state and actions
 */

import preloaderManager from '@/utils/preloaderManager';

const state = {
  isVisible: false,
  config: {
    brandName: 'الوساطة العقارية',
    brandIcon: 'mdi-home',
    logo: null,
    loadingText: 'Loading...',
    subText: '',
    spinnerType: 'circular',
    backgroundAnimation: 'gradient',
    showProgress: false,
    progress: null,
    steps: [],
    currentStep: 0,
    autoHide: false,
    autoHideDelay: 3000
  },
  progress: 0,
  currentStep: 0,
  steps: []
};

const mutations = {
  SET_VISIBLE(state, isVisible) {
    state.isVisible = isVisible;
  },
  
  SET_CONFIG(state, config) {
    state.config = { ...state.config, ...config };
  },
  
  SET_PROGRESS(state, progress) {
    state.progress = progress;
  },
  
  SET_CURRENT_STEP(state, step) {
    state.currentStep = step;
  },
  
  SET_STEPS(state, steps) {
    state.steps = steps;
  },
  
  UPDATE_TEXT(state, { text, subText }) {
    state.config.loadingText = text;
    state.config.subText = subText;
  }
};

const actions = {
  showPreloader({ commit }, config = {}) {
    preloaderManager.show(config);
    commit('SET_VISIBLE', true);
    commit('SET_CONFIG', config);
  },
  
  hidePreloader({ commit }) {
    preloaderManager.hide();
    commit('SET_VISIBLE', false);
  },
  
  updatePreloaderText({ commit }, { text, subText }) {
    preloaderManager.updateText(text, subText);
    commit('UPDATE_TEXT', { text, subText });
  },
  
  updatePreloaderProgress({ commit }, progress) {
    preloaderManager.updateProgress(progress);
    commit('SET_PROGRESS', progress);
  },
  
  setPreloaderSteps({ commit }, steps) {
    preloaderManager.setSteps(steps);
    commit('SET_STEPS', steps);
    commit('SET_CURRENT_STEP', 0);
  },
  
  nextPreloaderStep({ commit, state }) {
    preloaderManager.nextStep();
    commit('SET_CURRENT_STEP', state.currentStep + 1);
  },
  
  setPreloaderCurrentStep({ commit }, step) {
    preloaderManager.setCurrentStep(step);
    commit('SET_CURRENT_STEP', step);
  },
  
  // Convenience actions for common scenarios
  showAppInit({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-home',
      loadingText: 'جاري تهيئة التطبيق...',
      subText: 'يرجى الانتظار بينما نقوم بإعداد كل شيء لك',
      spinnerType: 'circular',
      backgroundAnimation: 'gradient',
      steps: [
        'جاري تحميل بيانات المستخدم...',
        'جاري تهيئة المكونات...',
        'جاري إعداد التنقل...',
        'جاري تحضير لوحة التحكم...',
        'تقريباً جاهز...'
      ]
    });
  },
  
  showDataLoading({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      loadingText: 'جاري تحميل البيانات...',
      subText: 'جاري جلب أحدث المعلومات',
      spinnerType: 'dots',
      backgroundAnimation: 'particles',
      showProgress: true,
      progress: 0
    });
  },
  
  showPageTransition({ dispatch }, pageName = '') {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      loadingText: `جاري تحميل ${pageName}...`,
      subText: 'يرجى الانتظار',
      spinnerType: 'wave',
      backgroundAnimation: 'waves',
      autoHide: true,
      autoHideDelay: 2000
    });
  },
  
  showAuth({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      loadingText: 'جاري المصادقة...',
      subText: 'جاري التحقق من بيانات الاعتماد',
      spinnerType: 'pulse',
      backgroundAnimation: 'gradient',
      steps: [
        'جاري التحقق من بيانات الاعتماد...',
        'جاري تحميل ملف المستخدم...',
        'جاري إعداد الجلسة...',
        'جاري إعادة التوجيه...'
      ]
    });
  },
  
  showFileUpload({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      loadingText: 'جاري رفع الملف...',
      subText: 'يرجى عدم إغلاق هذه النافذة',
      spinnerType: 'circular',
      backgroundAnimation: 'particles',
      showProgress: true,
      progress: 0
    });
  },
  
  showThemeSwitch({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      loadingText: 'جاري تبديل المظهر...',
      subText: 'جاري تطبيق تفضيلاتك',
      spinnerType: 'pulse',
      backgroundAnimation: 'gradient',
      autoHide: true,
      autoHideDelay: 1000
    });
  },

  // Full page loading actions
  showFullPageLoading({ dispatch }, config = {}) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-home',
      loadingText: 'جاري التحميل...',
      subText: 'يرجى الانتظار بينما نقوم بمعالجة طلبك',
      spinnerType: 'circular',
      showProgress: false,
      progress: null,
      steps: [],
      currentStep: 0,
      autoHide: false,
      autoHideDelay: 3000,
      isFullPage: true,
      ...config
    });
  },

  showFullPageThemeSwitch({ dispatch }, isDark) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-palette',
      loadingText: isDark ? 'جاري التبديل إلى الوضع المظلم...' : 'جاري التبديل إلى الوضع المضيء...',
      subText: 'جاري تطبيق تفضيلات المظهر',
      spinnerType: 'ring',
      showProgress: false,
      progress: null,
      steps: [],
      currentStep: 0,
      autoHide: true,
      autoHideDelay: 1500,
      isFullPage: true,
      isDark: isDark
    });
  },

  showFullPageAppInit({ dispatch }) {
    dispatch('showPreloader', {
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-home',
      loadingText: 'جاري تهيئة التطبيق...',
      subText: 'جاري إعداد كل شيء لك',
      spinnerType: 'circular',
      showProgress: true,
      progress: 0,
      steps: [
        'جاري تحميل بيانات المستخدم...',
        'جاري تهيئة المكونات...',
        'جاري إعداد التنقل...',
        'جاري تحضير لوحة التحكم...',
        'تقريباً جاهز...'
      ],
      currentStep: 0,
      autoHide: false,
      autoHideDelay: 3000,
      isFullPage: true
    });
  }
};

const getters = {
  isPreloaderVisible: state => state.isVisible,
  preloaderConfig: state => state.config,
  preloaderProgress: state => state.progress,
  preloaderCurrentStep: state => state.currentStep,
  preloaderSteps: state => state.steps
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
